import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSceneMatchingStatus, setSceneTimeOffset, fetchCurrentTime } from '../../../services/apis/StudioApi';

const SceneSettingsPage = () => {
    const { sceneId } = useParams();
    const navigate = useNavigate();

    const [matchingStatus, setMatchingStatus] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [offsetSeconds, setOffsetSeconds] = useState(0);
    const [minutes, setMinutes] = useState(0);
    const [seconds, setSeconds] = useState(0);
    const [direction, setDirection] = useState('positive'); // 'positive' (Fast) or 'negative' (Slow)
    const [cameraTime, setCameraTime] = useState('');
    const [serverTime, setServerTime] = useState('');
    const [isRematching, setIsRematching] = useState(false);
    const [serverClock, setServerClock] = useState(null);
    const serverTimeDiff = React.useRef(0);

    useEffect(() => {
        fetchMatchingStatus();
        initServerClock();
        return () => clearInterval(window.serverClockInterval);
    }, [sceneId]);

    const initServerClock = async () => {
        try {
            const res = await fetchCurrentTime();
            if (res.data && res.data.time) {
                const serverDate = new Date(res.data.time);
                const clientDate = new Date();
                serverTimeDiff.current = serverDate.getTime() - clientDate.getTime();

                // Update clock immediately
                updateServerClock();

                // Start interval
                window.serverClockInterval = setInterval(updateServerClock, 1000);
            }
        } catch (error) {
            console.error("Failed to fetch server time", error);
        }
    };

    const updateServerClock = () => {
        const now = new Date();
        const serverNow = new Date(now.getTime() + serverTimeDiff.current);
        setServerClock(serverNow);
    };

    // Sync minutes/seconds when offsetSeconds changes (e.g. from API or Calculator)
    useEffect(() => {
        setMinutes(Math.floor(offsetSeconds / 60));
        setSeconds(offsetSeconds % 60);
    }, [offsetSeconds]);

    const fetchMatchingStatus = async () => {
        setIsLoading(true);
        try {
            const res = await getSceneMatchingStatus(sceneId);
            setMatchingStatus(res.data);
            const currentOffset = res.data.scene.time_offset_seconds || 0;
            setOffsetSeconds(Math.abs(currentOffset));
            // If offset is negative, it means we are subtracting time, so camera is Fast (positive direction)
            setDirection(currentOffset < 0 ? 'positive' : 'negative');
        } catch (error) {
            console.error("Failed to fetch matching status", error);
            alert("매칭 현황을 불러오는데 실패했습니다.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleApplyOffset = async () => {
        const totalSeconds = (minutes * 60) + seconds;
        // If camera is Fast (positive), we subtract time (negative offset)
        const finalOffset = direction === 'positive' ? -totalSeconds : totalSeconds;

        if (!window.confirm(`보정값을 ${finalOffset}초로 설정하시겠습니까?\n모든 사진이 자동으로 재매칭됩니다.`)) {
            return;
        }

        setIsRematching(true);
        try {
            await setSceneTimeOffset(sceneId, finalOffset);
            alert("보정값이 적용되었습니다. 재매칭이 시작됩니다.");
            setTimeout(fetchMatchingStatus, 3000);
        } catch (error) {
            console.error("Failed to set time offset", error);
            alert("보정값 설정에 실패했습니다.");
            setIsRematching(false);
        }
    };

    const calculateOffset = () => {
        if (!cameraTime || !serverTime) return;
        const camera = new Date(`2000-01-01T${cameraTime}:00`);
        const server = new Date(`2000-01-01T${serverTime}:00`);
        const diffMs = camera.getTime() - server.getTime();
        const diffSeconds = Math.round(diffMs / 1000);

        setOffsetSeconds(Math.abs(diffSeconds));
        setDirection(diffSeconds < 0 ? 'negative' : 'positive');
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '없음';
        return new Date(dateStr).toLocaleString('ko-KR', {
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', hour12: false
        });
    };

    const formatTime = (dateStr) => {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleTimeString('ko-KR', {
            hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
        });
    };

    if (isLoading && !matchingStatus) return <div>Loading...</div>;
    if (!matchingStatus) return <div>Scene not found</div>;

    const { scene, photos, unmatched_photos } = matchingStatus;

    return (
        <div className="p-8 max-w-7xl mx-auto">
            <div className="flex justify-between items-center mb-8 border-b pb-4">
                <div className="flex items-center gap-4">
                    <span className="text-gray-500 text-lg pt-2 mr-2">시간 보정</span>
                    <div className="text-3xl font-bold">촬영: {scene.name}</div>
                </div>
                <div
                    onClick={() => navigate(-1)}
                    className="cursor-pointer bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded text-gray-700"
                >
                    뒤로가기
                </div>
            </div>

            {/* Matching Status Card */}
            <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                <h3 className="text-lg font-bold mb-4">📊 매칭 현황</h3>
                <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="p-4 bg-gray-50 rounded">
                        <div className="text-gray-500">전체 사진</div>
                        <div className="text-2xl font-bold">{photos.total}장</div>
                    </div>
                    <div className="p-4 bg-blue-50 rounded">
                        <div className="text-blue-500">매칭 완료</div>
                        <div className="text-2xl font-bold text-blue-600">
                            {photos.matched}장 ({photos.total > 0 ? Math.round((photos.matched / photos.total) * 100) : 0}%)
                        </div>
                    </div>
                    <div className="p-4 bg-red-50 rounded">
                        <div className="text-red-500">미매칭</div>
                        <div className="text-2xl font-bold text-red-600">{photos.unmatched}장</div>
                    </div>
                </div>
            </div>

            {/* Time Offset Settings */}
            <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                <h3 className="text-lg font-bold mb-4">⏱️ 카메라 시간 보정</h3>

                <div className="mb-6 p-4 bg-yellow-50 rounded border border-yellow-100">
                    <div className="flex justify-between items-center mb-2">
                        <span>현재 보정값: <strong>{scene.time_offset_seconds}초</strong> ({scene.time_offset_seconds < 0 ? '빠름 (시간 뺌)' : scene.time_offset_seconds > 0 ? '느림 (시간 더함)' : '보정 없음'})</span>
                        <span className="text-sm text-gray-500">
                            마지막 설정: {formatDate(scene.offset_calibrated_at)}
                        </span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-yellow-200">
                        <span className="text-yellow-800 font-bold">📡 현재 서버 시간</span>
                        <span className="text-xl font-mono font-bold text-yellow-900">
                            {serverClock ? serverClock.toLocaleString('ko-KR', {
                                hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
                            }) : 'Loading...'}
                        </span>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                    {/* Manual Input */}
                    <div>
                        <h4 className="font-semibold mb-4 text-gray-700">직접 입력</h4>
                        <div className="flex items-center gap-3 mb-2">
                            <div className="flex items-center">
                                <input
                                    type="number"
                                    value={minutes}
                                    onChange={(e) => setMinutes(Math.max(0, Number(e.target.value)))}
                                    className="border rounded p-2 w-20 text-right text-lg"
                                    placeholder="0"
                                />
                                <span className="ml-2 font-medium">분</span>
                            </div>
                            <div className="flex items-center">
                                <input
                                    type="number"
                                    value={seconds}
                                    onChange={(e) => setSeconds(Math.max(0, Number(e.target.value)))}
                                    className="border rounded p-2 w-20 text-right text-lg"
                                    placeholder="0"
                                />
                                <span className="ml-2 font-medium">초</span>
                            </div>

                            <div className="flex items-center ml-4">
                                <select
                                    value={direction}
                                    onChange={(e) => setDirection(e.target.value)}
                                    className="border rounded p-2 pr-8 text-lg min-w-[140px]"
                                >
                                    <option value="positive">카메라가 빠름 (-)</option>
                                    <option value="negative">카메라가 느림 (+)</option>
                                </select>
                            </div>
                        </div>
                        <p className="text-sm text-gray-500 mt-2">
                            * 카메라 시간이 서버 시간보다 {direction === 'positive' ? '빠를' : '느릴'} 때 선택하세요.
                            <br />
                            ({direction === 'positive' ? '시간을 뺍니다' : '시간을 더합니다'}: {(minutes * 60) + seconds}초)
                        </p>
                    </div>

                    {/* Calculator */}
                    <div className="border-l pl-12">
                        <h4 className="font-semibold mb-4 text-gray-700">시간 차이 계산기</h4>
                        <div className="flex items-end gap-3 mb-2">
                            <div>
                                <label className="block text-xs text-gray-500 mb-1">카메라 시간</label>
                                <input
                                    type="time"
                                    value={cameraTime}
                                    onChange={(e) => setCameraTime(e.target.value)}
                                    className="border rounded p-2"
                                />
                            </div>
                            <span className="pb-3 font-bold">-</span>
                            <div>
                                <label className="block text-xs text-gray-500 mb-1">서버 시간</label>
                                <input
                                    type="time"
                                    value={serverTime}
                                    onChange={(e) => setServerTime(e.target.value)}
                                    className="border rounded p-2"
                                />
                            </div>
                            <button
                                onClick={calculateOffset}
                                className="bg-gray-200 px-4 py-2 rounded hover:bg-gray-300 font-medium ml-2"
                            >
                                계산
                            </button>
                        </div>
                    </div>
                </div>

                <div className="mt-10 flex justify-end border-t pt-6">
                    <div
                        onClick={!isRematching ? handleApplyOffset : undefined}
                        className={`px-6 py-3 rounded text-white font-bold cursor-pointer ${isRematching ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'}`}
                    >
                        {isRematching ? '재매칭 중...' : '보정값 적용 및 재매칭'}
                    </div>
                </div>
            </div>

            {/* Unmatched Photos Preview */}
            {unmatched_photos && unmatched_photos.length > 0 && (
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-bold mb-4">🖼️ 미매칭 사진 (미리보기)</h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        {unmatched_photos.slice(0, 10).map(photo => (
                            <div key={photo.id} className="border rounded p-2">
                                <div className="aspect-w-1 aspect-h-1 bg-gray-200 mb-2 rounded overflow-hidden">
                                    {photo.thumbnail ? (
                                        <img src={photo.thumbnail} alt={`Unmatched ${photo.id}`} className="object-cover w-full h-full" />
                                    ) : (
                                        <div className="flex items-center justify-center h-24 text-gray-400">No Image</div>
                                    )}
                                </div>
                                <div className="text-xs text-gray-600">
                                    <div>EXIF: {formatTime(photo.exif_taken_at)}</div>
                                    <div className="text-blue-600">보정: {formatTime(photo.adjusted_taken_at)}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SceneSettingsPage;
